
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { 
  fetchAppItems, 
  fetchAppFavorites, 
  toggleAppFavorite,
  type AppItem,
  type AppFavorite
} from './database-apps';

/**
 * React Query hook to fetch app items
 */
export const useFetchAppItems = () => {
  return useQuery({
    queryKey: ['app-items'],
    queryFn: fetchAppItems,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Reduce retries to prevent suspension
    refetchOnWindowFocus: false,
  });
};

/**
 * React Query hook to fetch user favorites for apps
 */
export const useFetchAppFavorites = () => {
  return useQuery({
    queryKey: ['app-favorites'],
    queryFn: fetchAppFavorites,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

/**
 * React Query hook to toggle app favorite status
 */
export const useToggleAppFavorite = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ appId, isFavorited }: { appId: string; isFavorited: boolean }) => {
      return toggleAppFavorite(appId, isFavorited);
    },
    onSuccess: () => {
      // Invalidate related queries to refetch data and update favorite counts
      queryClient.invalidateQueries({ queryKey: ['app-favorites'] });
      queryClient.invalidateQueries({ queryKey: ['app-items'] }); // This will refetch with updated favorite_count
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update app favorite. Please try again.",
        variant: "destructive",
      });
      console.error('Toggle app favorite error:', error);
    },
  });
};
